import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  Input,
  HostListener,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { SubSink } from 'SubSink';
import { TaskAssistantService } from '@pm-ui/pm-services';

export interface ChartData {
  graphType: string;
  title: string;
  labels: string[];
  values: number[];
  orientation?: string;
  summary: string;
  keyFindings?: string[];
  recommendedActions?: string[];
  confidenceNote?: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  isLoading?: boolean;
  data?: any[];
  error?: boolean;
  chartData?: ChartData;
  tokenUsage?: number;
}

@Component({
  selector: 'pm-ui-ai-chatbot',
  templateUrl: './ai-chatbot.component.html',
  styleUrls: ['./ai-chatbot.component.scss'],
})
export class AiChatbotComponent implements OnInit, OnDestroy, AfterViewChecked, OnChanges {
  @Input() siteId: number = 0;
  @Input() siteCode: string = '';
  @Input() isMasterAdmin: boolean = false;
  @Input() selectedTaskIrn?: string;
  @Input() taskId?: number;
  @Input() subtaskId?: number;

  @ViewChild('messagesContainer') messagesContainer!: ElementRef;
  @ViewChild('inputField') inputField!: ElementRef;
  @ViewChild('chatbotWrapper') chatbotWrapper!: ElementRef;

  private subSink = new SubSink();
  isOpen = false;
  isMinimized = false;
  isMaximized = false;
  isLoading = false;
  private preMaximizePosition: { left: number; top: number } | null = null;
  private preMaximizeSize: { width: number; height: number } | null = null;

  private activeRequest: any = null;

  stopGeneration(): void {
    if (this.activeRequest) {
      this.activeRequest.unsubscribe();
      this.activeRequest = null;
      this.isLoading = false;
      this.messages = this.messages.filter((m) => !m.isLoading);
      this.addMessage({
        role: 'assistant',
        text: 'Generation stopped by user.',
        timestamp: new Date(),
        error: true,
      });
    }
  }

  // ── Drag state ───────────────────────────────────────────────────
  isDragging = false;
  dragPosition: { left: number; top: number } | null = null;
  private dragOffset = { x: 0, y: 0 };

  // ── Resize state ─────────────────────────────────────────────────
  isResizing = false;
  private resizeHandle = '';
  private resizeStart = { x: 0, y: 0, w: 0, h: 0, left: 0, top: 0 };
  panelSize: { width: number; height: number } | null = null;
  readonly MIN_W = 300;
  readonly MIN_H = 360;
  readonly MAX_W = 1100;
  readonly MAX_H = 900;

  questionCtrl = new FormControl('');
  messages: ChatMessage[] = [];
  suggestedQuestions: string[] = [];

  private shouldScrollToBottom = false;

  constructor(private taskAssistantService: TaskAssistantService) {}

  ngOnInit(): void {
    this.setupTaskOrGlobalContext();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['taskId'] || changes['subtaskId'] || changes['selectedTaskIrn']) {
      this.setupTaskOrGlobalContext();
    }
  }

  private setupTaskOrGlobalContext(): void {
    const isTaskScoped = (this.taskId && this.taskId > 0) || (this.subtaskId && this.subtaskId > 0);
    if (isTaskScoped) {
      this.suggestedQuestions = [
        'When was this task approved?',
        'What actions have been taken on this task?',
        'find other similar tasks to this one.',
        'find similar tasks to this one in building xyz',
        'find similar tasks to this one in the last 6 months',
      ];
    } else {
      this.suggestedQuestions = [
        'Show my open tasks for this site',
        'How many tasks are overdue this month?',
        'List all high priority tasks',
        'Show tasks raised in the last 7 days',
        'Which tasks are assigned to me?',
      ];
    }

    if (this.messages.length === 0) {
      this.addWelcomeMessage();
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.isMinimized = false;
      this.shouldScrollToBottom = true;
      setTimeout(() => this.inputField?.nativeElement?.focus(), 200);
    } else {
      this.resetPosition();
    }
  }

  private resetPosition(): void {
    this.dragPosition = null;
    this.isMaximized = false;
    this.isMinimized = false;
    if (this.chatbotWrapper?.nativeElement) {
      const el = this.chatbotWrapper.nativeElement as HTMLElement;
      el.style.left = '';
      el.style.top = '';
      el.style.bottom = '';
      el.style.right = '';
    }
    this.panelSize = null;
  }

  minimizeChat(): void {
    this.isMinimized = !this.isMinimized;
    if (this.isMinimized) {
      this.isMaximized = false;
    }
  }

  maximizeChat(): void {
    this.isMaximized = !this.isMaximized;
    if (this.isMaximized) {
      this.isMinimized = false;
      this.preMaximizePosition = this.dragPosition;
      this.preMaximizeSize = this.panelSize;
      this.dragPosition = null;
      this.panelSize = null;
    } else {
      this.dragPosition = this.preMaximizePosition;
      this.panelSize = this.preMaximizeSize;
    }
    this.shouldScrollToBottom = true;
  }

  closeChat(): void {
    this.isOpen = false;
    this.resetPosition();
  }

  onHeaderMouseDown(event: MouseEvent): void {
    if ((event.target as HTMLElement).closest('button')) return;

    if (!this.dragPosition && this.chatbotWrapper) {
      const rect = (this.chatbotWrapper.nativeElement as HTMLElement).getBoundingClientRect();
      this.dragPosition = { left: rect.left, top: rect.top };
    }

    this.isDragging = true;
    this.dragOffset = {
      x: event.clientX - (this.dragPosition?.left ?? 0),
      y: event.clientY - (this.dragPosition?.top ?? 0),
    };
    event.preventDefault();
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
    this.isResizing = false;
  }

  onResizeMouseDown(event: MouseEvent, handle: string): void {
    event.stopPropagation();
    event.preventDefault();
    const panel = this.chatbotWrapper?.nativeElement?.querySelector('.chat-panel') as HTMLElement;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const wrapperRect = (this.chatbotWrapper.nativeElement as HTMLElement).getBoundingClientRect();
    this.isResizing = true;
    this.resizeHandle = handle;
    this.resizeStart = {
      x: event.clientX,
      y: event.clientY,
      w: rect.width,
      h: rect.height,
      left: wrapperRect.left,
      top: wrapperRect.top,
    };
    if (!this.dragPosition) {
      this.dragPosition = { left: wrapperRect.left, top: wrapperRect.top };
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      event.preventDefault();
      const panelW = this.panelSize?.width ?? (this.isMaximized ? Math.min(920, window.innerWidth - 40) : 380);
      const panelH = this.panelSize?.height ?? (this.isMaximized ? Math.min(680, window.innerHeight - 40) : 540);
      const left = Math.max(0, Math.min(event.clientX - this.dragOffset.x, window.innerWidth - panelW));
      const top = Math.max(0, Math.min(event.clientY - this.dragOffset.y, window.innerHeight - panelH));
      this.dragPosition = { left, top };
      return;
    }

    if (!this.isResizing) return;
    event.preventDefault();
    const dx = event.clientX - this.resizeStart.x;
    const dy = event.clientY - this.resizeStart.y;
    let newW = this.resizeStart.w;
    let newH = this.resizeStart.h;
    let newLeft = this.dragPosition?.left ?? this.resizeStart.left;
    let newTop  = this.dragPosition?.top  ?? this.resizeStart.top;

    if (this.resizeHandle.includes('e')) {
      const maxW = window.innerWidth - newLeft - 10;
      newW = Math.max(this.MIN_W, Math.min(this.MAX_W, maxW, this.resizeStart.w + dx));
    }
    if (this.resizeHandle.includes('s')) {
      const maxH = window.innerHeight - newTop - 10;
      newH = Math.max(this.MIN_H, Math.min(this.MAX_H, maxH, this.resizeStart.h + dy));
    }
    if (this.resizeHandle.includes('w')) {
      const maxW = this.resizeStart.left + this.resizeStart.w - 10;
      const candidate = Math.max(this.MIN_W, Math.min(this.MAX_W, maxW, this.resizeStart.w - dx));
      newLeft = this.resizeStart.left + (this.resizeStart.w - candidate);
      newW = candidate;
    }
    if (this.resizeHandle.includes('n')) {
      const maxH = this.resizeStart.top + this.resizeStart.h - 10;
      const candidate = Math.max(this.MIN_H, Math.min(this.MAX_H, maxH, this.resizeStart.h - dy));
      newTop = this.resizeStart.top + (this.resizeStart.h - candidate);
      newH = candidate;
    }

    this.panelSize = { width: newW, height: newH };
    this.dragPosition = { left: newLeft, top: newTop };
    this.isMaximized = false;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.isOpen || this.isMaximized) return;
    if (this.dragPosition) {
      const panelW = this.panelSize?.width ?? 380;
      const panelH = this.panelSize?.height ?? 540;

      const maxLeft = window.innerWidth - panelW;
      let left = this.dragPosition.left;
      if (left > maxLeft) left = Math.max(0, maxLeft);
      if (left < 0) left = 0;

      const maxTop = window.innerHeight - panelH;
      let top = this.dragPosition.top;
      if (top > maxTop) top = Math.max(0, maxTop);
      if (top < 0) top = 0;

      this.dragPosition = { left, top };
    }
  }

  getWrapperStyle(): Record<string, string> {
    if (this.isMaximized) {
      return {
        'position': 'fixed',
        'bottom': '0',
        'right': '0',
        'left': '0',
        'top': '0',
        'width': '100%',
        'height': '100%',
      };
    }
    const style: Record<string, string> = {};
    if (this.dragPosition) {
      style['bottom'] = 'auto';
      style['right']  = 'auto';
      style['left']   = this.dragPosition.left + 'px';
      style['top']    = this.dragPosition.top  + 'px';
    }
    return style;
  }

  getPanelStyle(): Record<string, string> {
    if (this.isMaximized) {
      return {
        'width': '100%',
        'height': '100%',
      };
    }
    if (this.panelSize && !this.isMaximized) {
      return {
        width:  this.panelSize.width  + 'px',
        height: this.panelSize.height + 'px',
      };
    }
    return {};
  }

  adjustTextareaHeight(): void {
    const textarea = this.inputField?.nativeElement;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 80) + 'px';
    }
  }

  askSuggested(question: string): void {
    this.questionCtrl.setValue(question);
    this.sendMessage();
  }

  sendMessage(): void {
    const question = (this.questionCtrl.value || '').trim();
    if (!question || this.isLoading) return;

    if (!this.siteId || this.siteId === 0) {
      this.addMessage({
        role: 'assistant',
        text: '⚠️ Please select a site first before asking questions.',
        timestamp: new Date(),
        error: true,
      });
      return;
    }

    this.addMessage({ role: 'user', text: question, timestamp: new Date() });
    this.questionCtrl.setValue('');
    setTimeout(() => this.adjustTextareaHeight(), 0);

    if (this.isOffTopicQuery(question)) {
      this.addMessage({
        role: 'assistant',
        text: `Hello! I am your AI Task Assistant. I can help you search and view tasks, but I cannot answer off-topic questions.`,
        timestamp: new Date(),
      });
      return;
    }

    this.isLoading = true;

    const loadingMsg: ChatMessage = {
      role: 'assistant',
      text: '',
      timestamp: new Date(),
      isLoading: true,
    };
    this.messages.push(loadingMsg);
    this.shouldScrollToBottom = true;

    const chatHistory = this.messages
      .filter((m) => !m.isLoading && !m.error && m.text && m.text.trim().length > 0)
      .slice(-7, -1)
      .map((m) => {
        let text = m.text;
        if (m.role === 'assistant' && m.data && m.data.length > 0) {
          const cols = Object.keys(m.data[0]);
          const preview = m.data.slice(0, 10).map((row: any, ri: number) =>
            `Row ${ri + 1}: ${cols.map(c => `${c}=${row[c]}`).join(', ')}`
          ).join(' | ');
          text = `${m.text} [Data returned: ${m.data.length} rows. Preview: ${preview}${m.data.length > 10 ? ` ... and ${m.data.length - 10} more rows` : ''}]`;
        }
        return { role: m.role as 'user' | 'assistant', text };
      });

    if (this.selectedTaskIrn) {
      chatHistory.push({ role: 'user', text: `[System Context: The user currently has task IRN ${this.selectedTaskIrn} highlighted/selected in the UI. If they ask about "this task", they mean ${this.selectedTaskIrn}]` });
    }

    this.activeRequest = this.taskAssistantService
      .query({
        question,
        siteId: this.siteId,
        chatHistory,
        taskId: this.taskId,
        subtaskId: this.subtaskId
      })
      .subscribe({
        next: (response: any) => {
          this.activeRequest = null;
          this.messages = this.messages.filter((m) => !m.isLoading);
          this.isLoading = false;
          const payload = response?.body ?? response;
          this.addMessage({
            role: 'assistant',
            text: payload?.answer || 'No answer returned. Please try rephrasing your question.',
            timestamp: new Date(),
            data: payload?.data,
            chartData: payload?.chartData ?? undefined,
            tokenUsage: payload?.tokenUsage ?? undefined,
          });
        },
        error: (err: any) => {
          this.activeRequest = null;
          this.messages = this.messages.filter((m) => !m.isLoading);
          this.isLoading = false;
          const errMsg =
            err?.error?.message || err?.message || 'Something went wrong. Please try again.';
          this.addMessage({
            role: 'assistant',
            text: errMsg,
            timestamp: new Date(),
            error: true,
          });
        },
      });

    this.subSink.sink = this.activeRequest;
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  clearHistory(): void {
    this.messages = [];
    this.addWelcomeMessage();
  }

  newChat(): void {
    if (this.activeRequest) {
      this.activeRequest.unsubscribe();
      this.activeRequest = null;
    }
    this.isLoading = false;
    this.clearHistory();
    this.questionCtrl.setValue('');
    setTimeout(() => this.adjustTextareaHeight(), 0);
  }

  hasTableData(msg: ChatMessage): boolean {
    if (!msg.data || msg.data.length === 0) return false;
    if (this.hasIRNColumn(msg.data)) {
      return msg.data.length > 1;
    }
    return true;
  }

  isRagContext(msg: ChatMessage): boolean {
    return !!(msg.data && msg.data.length > 0 && msg.data[0].IsRagContext);
  }

  getTableColumns(data: any[]): string[] {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]).filter(col => {
      if (col.toLowerCase() === 'emailid') return true;
      return !col.endsWith('Id') && !col.endsWith('ID') && col !== 'id' && col !== 'Id' && col !== 'ID';
    });
  }

  hasIRNColumn(data: any[] | undefined): boolean {
    if (!data || data.length === 0) return false;
    return Object.keys(data[0]).some(k => k.toLowerCase() === 'irn');
  }

  getTaskUrl(row: any): string {
    const irn = row.IRN || row.irn;
    const taskId = row.TaskId || row.taskId;
    const subTaskId = row.SubTaskId || row.subTaskId;

    if (!taskId) {
      const taskSiteId = row.SiteId || row.siteId || this.siteId;
      return `${window.location.origin}/admin/task-manager?SiteId=${taskSiteId}&irn=${irn}`;
    }

    if (subTaskId) {
      const taskSiteId = row.SiteId || row.siteId || this.siteId;
      return `${window.location.origin}/subTask?taskId=${taskId}&subTaskId=${subTaskId}&siteId=${taskSiteId}&siteC=${this.siteCode}`;
    }

    const taskSiteId = row.SiteId || row.siteId || this.siteId;
    return `${window.location.origin}/task?taskId=${taskId}&siteId=${taskSiteId}&siteC=${this.siteCode}`;
  }

  formatTime(date: Date): string {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private addWelcomeMessage(): void {
    const isTaskScoped = (this.taskId && this.taskId > 0) || (this.subtaskId && this.subtaskId > 0);
    const welcomeText = isTaskScoped
      ? `Hello! I'm your AI Task Assistant for ${this.selectedTaskIrn ? 'task ' + this.selectedTaskIrn : 'this task'}. How can I help you today?`
      : `Hello! I'm your AI Task Assistant. How can I help you today?`;

    this.messages.push({
      role: 'assistant',
      text: welcomeText,
      timestamp: new Date(),
    });
  }

  private addMessage(msg: ChatMessage): void {
    this.messages.push(msg);
    this.shouldScrollToBottom = true;
  }

  private isOffTopicQuery(question: string): boolean {
    const q = question.toLowerCase().trim().replace(/[?!.,]+$/g, '');
    const offTopicPatterns = [
      /^hi+\b/,
      /^hello+\b/,
      /^hey+\b/,
      /^howdy\b/,
      /^good\s*(morning|afternoon|evening|night)\b/,
      /^how\s+(are|r)\s+(you|u)\b/,
      /^how\s+(you|u)\s+doing\b/,
      /^how('s|\s+is)\s+it\s+going\b/,
      /^what('s|\s+is)\s+up\b/,
      /^sup\b/,
      /^who\s+are\s+(you|u)\b/,
      /^what\s+can\s+(you|u)\s+do\b/,
      /^what\s+do\s+(you|u)\s+do\b/,
      /^tell\s+me\s+(a\s+joke|something|about\s+yourself)\b/,
      /^(joke|funny|laugh)\b/,
      /^(thanks|thank\s+you|ty|thx)\b/,
      /^(bye|goodbye|see\s+you|cya|later)\b/,
      /^(ok|okay|sure|alright|cool|nice|great)\b$/,
      /^(yes|no|maybe|yep|nope|nah|yeah)\b$/,
      /^what\s+is\s+(your\s+name|your\s+age|the\s+time|the\s+date|the\s+weather)\b/,
      /^(weather|news|sports|movie|music|food|recipe|love|life)\b/,
      /^(help\s+me\s+with|can\s+you\s+help|how\s+do\s+i)\b(?!.*task|.*report|.*site|.*assign|.*overdue|.*priority|.*property)/,
    ];
    if (offTopicPatterns.some((p) => p.test(q))) return true;

    const taskKeywords = [
      'task', 'tasks', 'subtask', 'subtasks', 'report', 'site', 'assign', 'overdue',
      'priority', 'open', 'closed', 'status', 'property', 'raised', 'due',
      'completed', 'completing', 'complete', 'pending', 'high', 'low', 'medium',
      'list', 'show', 'count', 'how many', 'latest', 'recent', 'last',
      'this week', 'this month',
      'electrical', 'plumbing', 'hvac', 'aircon', 'air con', 'hot water',
      'water', 'fire', 'security', 'cleaning', 'painting', 'mechanical',
      'equipment', 'doors', 'carpentry', 'comms', 'authority', 'building',
      'issue', 'problem', 'fault', 'repair', 'maintenance', 'give me', 'find me',
      'show me', 'what task', 'which task', 'all task', 'summarise', 'summarize',
      'history', 'actions', 'resolve', 'plan', 'suggest', 'similar',
      'recommend', 'recommendation', 'next step', 'action plan', 'what can we do',
      'how to fix', 'what should', 'advise', 'advice', 'strategy', 'approved', 'approval',
      'tenancy', 'level', 'floor', 'toilet', 'common area', 'carpark',
      'urgent', 'critical', 'overdue', 'action first', 'priority',
      'cost', 'how much', 'when was', 'when did', 'last time', 'replaced', 'frequency',
      'most common', 'frequent', 'category',
      'user', 'technician', 'assigned to', 'information about',
    ];
    const hasTaskKeyword = taskKeywords.some((kw) => q.includes(kw));
    const words = q.split(/\s+/);
    if (words.length <= 3 && !hasTaskKeyword && !q.includes('h20') && !q.includes('m20')) return true;

    return false;
  }

  private scrollToBottom(): void {
    try {
      this.messagesContainer.nativeElement.scrollTop =
        this.messagesContainer.nativeElement.scrollHeight;
    } catch (err) {}
  }

  getMaxChartValue(msg: ChatMessage): number {
    if (!msg.chartData?.values?.length) return 1;
    return Math.max(...msg.chartData.values);
  }

  getBarChartBars(msg: ChatMessage): { label: string; value: number; pct: number; color: string }[] {
    if (!msg.chartData) return [];
    const max = this.getMaxChartValue(msg);
    const colors = ['#6366f1','#8b5cf6','#06b6d4','#10b981','#f59e0b','#ef4444','#ec4899','#3b82f6'];
    return msg.chartData.labels.map((label, i) => ({
      label,
      value: msg.chartData!.values[i] ?? 0,
      pct: max > 0 ? Math.round(((msg.chartData!.values[i] ?? 0) / max) * 100) : 0,
      color: colors[i % colors.length],
    }));
  }

  exportToExcel(data: any[]): void {
    if (!data || data.length === 0) return;

    const columns = this.getTableColumns(data);
    const header = columns.join(',');
    const rows = data.map((row) =>
      columns
        .map((col) => {
          let val = row[col] === null || row[col] === undefined ? '' : row[col];
          val = val.toString().replace(/"/g, '""');
          return `"${val}"`;
        })
        .join(',')
    );

    const csvContent = [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.setAttribute('href', url);
    link.setAttribute('download', `AI_Assistant_Export_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  downloadConversation(): void {
    if (!this.messages || this.messages.length === 0) return;

    let content = `AI Task Assistant - Conversation Log\n`;
    content += `Generated on: ${new Date().toLocaleString()}\n`;
    content += `Site ID: ${this.siteId}\n`;
    content += `--------------------------------------------------\n\n`;

    this.messages.forEach((msg) => {
      if (msg.isLoading) return;

      const time = msg.timestamp ? msg.timestamp.toLocaleString() : '';
      const role = msg.role === 'user' ? 'USER' : 'AI ASSISTANT';

      content += `[${time}] ${role}:\n`;
      content += `${msg.text}\n`;

      if (msg.data && msg.data.length > 0) {
        content += `(Data returned: ${msg.data.length} rows)\n`;
        const cols = this.getTableColumns(msg.data);
        content += `Columns: ${cols.join(', ')}\n`;
        msg.data.slice(0, 3).forEach((row, ri) => {
          content += `  Row ${ri + 1}: ${cols.map(c => `${c}=${row[c]}`).join(', ')}\n`;
        });
        if (msg.data.length > 3) content += `  ... and ${msg.data.length - 3} more rows.\n`;
      }

      if (msg.chartData) {
        content += `(Chart: ${msg.chartData.title})\n`;
        content += `Summary: ${msg.chartData.summary}\n`;
      }

      content += `\n--------------------------------------------------\n\n`;
    });

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

    link.setAttribute('href', url);
    link.setAttribute('download', `AI_Conversation_Log_${timestamp}.txt`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
